<template>
  <!-- v-help appends its « ? » as the bar's last child — one trigger for the whole
       concept (cascade, pins, greyed options), not one per menu. -->
  <div v-if="hasAnyDropdown" v-help="'concepts.barre-de-contexte'" class="ctx-toolbar" role="toolbar"
    :aria-label="translate('parcours.context.toolbar.label')">
    <!-- Site -->
    <div v-if="showSite" class="ctx-item">
      <template v-if="!store.pinnedSite">
        <ContextDropdown
          :model-value="store.selectedSite?.id ?? null"
          :selected-label="store.selectedSite?.name ?? null"
          :placeholder="translate('parcours.context.axis.centre')"
          :menu-label="translate('parcours.context.axis.centre')"
          :sections="siteSections"
          :loading="store.loadingSites"
          :highlight="activeHighlight.includes('site')"
          @update:model-value="id => select('site', id)"
        />
        <button class="ctx-pin-btn" :title="translate('parcours.context.toolbar.pinSite')" :disabled="!store.selectedSite"
          @click="store.pinnedSite = true">
          <font-awesome-icon icon="thumbtack" />
        </button>
      </template>
      <template v-else>
        <button class="ctx-badge" :title="translate('parcours.context.toolbar.unpinSite')" @click="store.pinnedSite = false">
          <font-awesome-icon icon="building" class="ctx-badge__icon" />
          <span class="ctx-badge__text">{{ store.selectedSite?.name ?? translate('parcours.context.toolbar.allLabel') }}</span>
        </button>
      </template>
    </div>

    <span v-if="showSepBeforeProgram" class="ctx-sep" aria-hidden="true" />

    <!-- Program -->
    <div v-if="showProgram" class="ctx-item">
      <template v-if="!store.pinnedProgram">
        <!-- Sections with no entries are omitted, so a site with no group program does not
             show a « Groupes » heading followed by nothing (#775); the page guard, which has
             the room, explains why. -->
        <ContextDropdown
          :model-value="store.selectedProgram?.id ?? null"
          :selected-label="store.selectedProgram?.title ?? null"
          :placeholder="translate('parcours.context.axis.programme')"
          :menu-label="translate('parcours.context.axis.programme')"
          :sections="programSections"
          :loading="store.loadingPrograms"
          :highlight="activeHighlight.includes('program')"
          @update:model-value="id => select('program', id)"
        />
        <button class="ctx-pin-btn" :title="translate('parcours.context.toolbar.pinProgram')" :disabled="!store.selectedProgram"
          @click="store.pinnedProgram = true">
          <font-awesome-icon icon="thumbtack" />
        </button>
      </template>
      <template v-else>
        <button class="ctx-badge" :title="translate('parcours.context.toolbar.unpinProgram')" @click="store.pinnedProgram = false">
          <font-awesome-icon icon="box-archive" class="ctx-badge__icon" />
          <span class="ctx-badge__text">
            {{ store.selectedProgram?.title ?? translate('parcours.context.toolbar.allLabel') }}
          </span>
        </button>
      </template>
    </div>

    <span v-if="showSepBeforeGroup" class="ctx-sep" aria-hidden="true" />

    <!-- Group -->
    <div v-if="showGroup" class="ctx-item">
      <template v-if="!store.pinnedGroup">
        <ContextDropdown
          :model-value="store.selectedGroup?.id ?? null"
          :selected-label="groupTriggerLabel"
          :placeholder="translate('parcours.context.axis.groupe')"
          :menu-label="translate('parcours.context.axis.groupe')"
          :sections="groupSections"
          :loading="store.loadingGroups"
          :highlight="activeHighlight.includes('group')"
          @update:model-value="id => select('group', id)"
        />
        <button class="ctx-pin-btn" :title="translate('parcours.context.toolbar.pinGroup')" :disabled="!store.selectedGroup"
          @click="store.pinnedGroup = true">
          <font-awesome-icon icon="thumbtack" />
        </button>
      </template>
      <template v-else>
        <button class="ctx-badge" :title="translate('parcours.context.toolbar.unpinGroup')" @click="store.pinnedGroup = false">
          <font-awesome-icon icon="users" class="ctx-badge__icon" />
          <span class="ctx-badge__text">{{ selectedGroupBadgeLabel }}</span>
        </button>
      </template>
    </div>

    <!-- The specialist's « Groupe » (#1445): the groups of their students, a list of its own. No
         cascade to reset, nothing to pin, and no « none »: their page always shows a group. -->
    <div v-if="showSpecialistGroup" class="ctx-item">
      <ContextDropdown
        :model-value="specialistGroups.selectedId"
        :selected-label="specialistGroups.selected?.name ?? null"
        :placeholder="translate('parcours.context.axis.groupe')"
        :menu-label="translate('parcours.context.axis.groupe')"
        :hint="translate('parcours.specialist.groups.menuHint')"
        :clearable="false"
        :sections="specialistGroupSections"
        :loading="specialistGroups.loading && !specialistGroups.selected"
        @update:model-value="id => specialistGroups.choose(id)"
      />
    </div>

    <span v-if="showSepBeforeTeacher" class="ctx-sep" aria-hidden="true" />

    <!-- Teacher -->
    <div v-if="showTeacher" class="ctx-item">
      <template v-if="!store.pinnedTeacher">
        <ContextDropdown
          :model-value="store.selectedTeacher?.id ?? null"
          :selected-label="store.selectedTeacher
            ? `${store.selectedTeacher.user.firstName} ${store.selectedTeacher.user.lastName}` : null"
          :placeholder="translate('parcours.context.axis.enseignant')"
          :menu-label="translate('parcours.context.axis.enseignant')"
          :sections="teacherSections"
          :loading="store.loadingTeachers"
          :highlight="activeHighlight.includes('teacher')"
          @update:model-value="id => select('teacher', id)"
        />
        <button class="ctx-pin-btn" :title="translate('parcours.context.toolbar.pinTeacher')" :disabled="!store.selectedTeacher"
          @click="store.pinnedTeacher = true">
          <font-awesome-icon icon="thumbtack" />
        </button>
      </template>
      <template v-else>
        <button class="ctx-badge" :class="{ 'ctx-badge--locked': store.teacherFilterLockedToSelf }"
          :disabled="store.teacherFilterLockedToSelf"
          :title="store.teacherFilterLockedToSelf
            ? translate('parcours.context.toolbar.teacherLockedMessage')
            : translate('parcours.context.toolbar.unpinTeacher')"
          @click="store.teacherFilterLockedToSelf ? undefined : (store.pinnedTeacher = false)">
          <font-awesome-icon :icon="'chalkboard-user'" class="ctx-badge__icon" />
          <span class="ctx-badge__text">
            {{ store.selectedTeacher ? `${store.selectedTeacher.user.firstName} ${store.selectedTeacher.user.lastName}`
              : translate('parcours.context.toolbar.allLabel') }}
          </span>
        </button>
      </template>
    </div>

    <span v-if="showSepBeforeActivity" class="ctx-sep" aria-hidden="true" />

    <!-- Activity (#1163): the presence TYPE — Normal, Examen… — of the Présences sheet. A filter
         on the cells, not on whose sheet it is: outside the cascade, hence no pin. No « ? » of
         its own either: the bar's single one covers it, and a trigger here landed right beside
         that one, which the bar appends as its last child. -->
    <div v-if="showActivity" class="ctx-item">
      <ContextDropdown
        multiple
        :model-values="store.selectedActivityTypes"
        :selected-label="store.selectedActivityLabel"
        :placeholder="translate('parcours.context.axis.activity')"
        :menu-label="translate('parcours.context.axis.activity')"
        :sections="activitySections"
        :loading="store.loadingActivityTypes"
        :highlight="activeHighlight.includes('activity')"
        @update:model-values="keys => store.setActivityTypes(keys)"
      />
    </div>

    <span v-if="showSepBeforeUsers" class="ctx-sep" aria-hidden="true" />

    <!-- Student filter -->
    <div v-if="showUsers" class="ctx-item">
      <StudentFilter :model-value="store.studentFilter" @update:model-value="store.studentFilter = $event" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, watch, ref, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { translate } from '@fondationcss/sdk';
import { useContextToolbarStore } from '@/stores/context-toolbar-store';
import { useSpecialistGroupStore } from '@/stores/specialist-group-store';
import { groupSubtitle, specialistGroupMenuSections } from '@/views/specialist/specialist-groups';
import type { SpecialistGroup } from '@/sdk/dto/specialist';
import { useHashParams } from '@fondationcss/ui';
import { useSdk as useCoreSdk } from '@fondationcss/sdk';
import { useContextSelection } from '@/composables/use-context-selection';
import { programMenuSections, visibleContextAxes } from '@/composables/context-axis';
import { buildMenuItems, type ContextMenuSection } from '@/composables/context-menu-items';
import { isProgramScope, programScopeKind } from '@/utils/program-scope';
import { GroupStatus, type Group } from '@/sdk/model/group';
import type { LearningProgram } from '@/sdk/model/learning-program';
import StudentFilter from '@/components/student-filter.vue';
import ContextDropdown from '@/components/context-dropdown.vue';
import type { ContextDropdown as ContextDropdownName } from '@/routes';

const props = defineProps<{ highlight?: ContextDropdownName[] }>();

const store = useContextToolbarStore();
const specialistGroups = useSpecialistGroupStore();
const route = useRoute();
const hashParams = useHashParams();
const { select } = useContextSelection();

// The menus' four partitions, moved out of the template: each is now read twice
// (the heading's `v-if`, then the entries' `v-for`), and refiltering the list on
// every render for nothing is pointless.
/**
 * The kind of program the route imposes, if any. Three screens only admit one kind and yet
 * offered both — the rule and its reason live in `programMenuSections`, which is tested.
 */
const requiredProgramType = computed(
  () => (route.meta as { programType?: 'individual' | 'group' }).programType
);

// #838 — two directions, two treatments. Left to right, the cascade keeps FILTERING:
// the menus offer only what fits the selections upstream (a menu of mostly-greyed
// entries proved unusable — the lists are long). Right to left, a PIN disables the
// incompatible options in the other menus and marks the compatible ones with the
// thumbtack. Item construction lives in `buildMenuItems`, tested apart.
const PIN_REASON = computed(() => translate('parcours.context.toolbar.outOfContext'));

const siteSections = computed<ContextMenuSection[]>(() => [{
  items: buildMenuItems(store.sites, {
    id: s => s.id,
    label: s => s.name,
    enabledIds: store.enabledSiteIds,
    pinReason: PIN_REASON.value,
  }),
}]);

const programSections = computed<ContextMenuSection[]>(() => {
  const options = {
    id: (p: LearningProgram) => p.id,
    label: (p: LearningProgram) => p.title,
    enabledIds: store.enabledProgramIds,
    pinReason: PIN_REASON.value,
    scope: (p: LearningProgram) => isProgramScope(p),
  };
  // The family entries ARE the headings (#948) — see programMenuSections.
  return programMenuSections(
    store.filteredPrograms,
    requiredProgramType.value,
    store.selectedProgram?.id,
    p => programScopeKind(p),
  ).map(section => ({ label: section.label, items: buildMenuItems(section.entries, options) }));
});

const groupMenuOptions = computed(() => ({
  id: (g: Group) => g.id,
  label: (g: Group) => g.name,
  enabledIds: store.enabledGroupIds,
  pinReason: PIN_REASON.value,
}));

const groupSections = computed<ContextMenuSection[]>(() => {
  if (store.groupStatusFilter === 'all') {
    const confirmed = store.filteredGroups.filter(g => g.status === GroupStatus.Confirmed);
    const unconfirmed = store.filteredGroups.filter(g => g.status === GroupStatus.Unconfirmed);
    const sections: ContextMenuSection[] = [];
    if (confirmed.length) sections.push({ label: translate('parcours.context.toolbar.groupConfirmed'), items: buildMenuItems(confirmed, groupMenuOptions.value) });
    if (unconfirmed.length) sections.push({ label: translate('parcours.context.toolbar.groupUnconfirmed'), items: buildMenuItems(unconfirmed, groupMenuOptions.value) });
    return sections;
  }
  const suffix = store.groupStatusFilter === GroupStatus.Unconfirmed ? translate('parcours.context.toolbar.groupUnconfirmedSuffix') : '';
  return [{
    items: buildMenuItems(store.filteredGroups, {
      ...groupMenuOptions.value,
      label: (g: Group) => `${g.name}${suffix}`,
    }),
  }];
});

const teacherSections = computed<ContextMenuSection[]>(() => [{
  items: buildMenuItems(store.filteredTeachers, {
    id: t => t.id,
    label: t => `${t.user.firstName} ${t.user.lastName}`,
    enabledIds: store.enabledTeacherIds,
    pinReason: PIN_REASON.value,
  }),
}]);

// A section per program, each group saying how many of its students are the specialist's: the
// reason it is in their list.
const specialistGroupSections = computed<ContextMenuSection[]>(() =>
  specialistGroupMenuSections(specialistGroups.groups).map(section => ({
    label: section.label,
    items: buildMenuItems(section.entries, {
      id: (g: SpecialistGroup) => g.id,
      label: (g: SpecialistGroup) => g.name,
      detail: (g: SpecialistGroup) => groupSubtitle(g),
    }),
  })));

// The catalogue, in its own order; no pin constraint, the axis being outside the cascade.
const activitySections = computed<ContextMenuSection[]>(() => [{
  items: buildMenuItems(store.activityTypes, { id: t => t.key, label: t => t.label }),
}]);

// Debounced highlight (#315). The pulse is driven by `ctxStore.highlight`, a shared ref the
// page guards (ContextRequired/ReportForm) rewrite on mount. During
// navigation a required context is momentarily "unsatisfied" before the selection settles
// (async hash apply, store cascade resets, group-status-filter switch), so the highlight
// blips `[] → [x] → []` and a dropdown pulses for a fraction of a second — a flash with no
// real reason. Only reflect a value that has stayed stable for a short window: transient
// blips are swallowed, a genuinely missing context still pulses after the delay.
// Clearing is immediate so a pulse never leaks into a page/tab that no longer
// requests attention.
const HIGHLIGHT_SETTLE_MS = 300;
const activeHighlight = ref<ContextDropdownName[]>([]);
let highlightTimer: ReturnType<typeof setTimeout> | null = null;

function sameHighlight(a: ContextDropdownName[], b: ContextDropdownName[]) {
  return a.length === b.length && a.every((item, index) => item === b[index]);
}

watch(
  () => (props.highlight ?? []).join('|'),
  () => {
    if (highlightTimer) clearTimeout(highlightTimer);
    const nextHighlight = props.highlight ?? [];
    if (nextHighlight.length === 0 || !sameHighlight(activeHighlight.value, nextHighlight)) {
      activeHighlight.value = [];
    }
    if (nextHighlight.length === 0) {
      highlightTimer = null;
      return;
    }
    highlightTimer = setTimeout(() => {
      activeHighlight.value = nextHighlight;
      highlightTimer = null;
    }, HIGHLIGHT_SETTLE_MS);
  },
  { immediate: true }
);

onBeforeUnmount(() => { if (highlightTimer) clearTimeout(highlightTimer); });

// Reset the group status filter to the route's default whenever the active route changes.
watch(
  () => route.name,
  () => {
    const meta = route.meta as { groupStatusDefault?: 'confirmed' | 'unconfirmed' | 'all' };
    const default_ = meta.groupStatusDefault ?? 'confirmed';
    const newFilter = default_ === 'all'
      ? 'all'
      : default_ === 'unconfirmed'
        ? GroupStatus.Unconfirmed
        : GroupStatus.Confirmed;
    store.switchGroupStatusFilter(newFilter);
  },
  { immediate: true }
);

const coreSdk = useCoreSdk();

const visibleDropdowns = computed<ContextDropdownName[]>(() => {
  const declared = (route.meta.contextToolbar as ContextDropdownName[] | undefined)
    ?? ['site', 'program', 'group', 'teacher'];
  // A page may narrow its route's list to what it currently reads — the Rapports pages do, per
  // chosen report (#924). Checked against the route it was set for, so a restriction left behind
  // by a navigation cannot strip another screen's dropdowns. It only ever removes.
  const restriction = store.axisRestriction;
  const axes = restriction && store.axisRestrictionRoute === route.name
    ? declared.filter(axis => restriction.includes(axis))
    : declared;
  // #812: with « Formation par groupes » off, the Groupe axis withdraws from every route —
  // the mixed screens (Présences, Examens, rapports) keep their other axes untouched. #1164: the
  // exam supervisor gets none at all, every list behind them answering 403.
  return visibleContextAxes(axes, coreSdk.Features.flags.value, store.examSupervisorOnly);
});

const showSite = computed(() => visibleDropdowns.value.includes('site'));
const showProgram = computed(() => visibleDropdowns.value.includes('program'));
const showGroup = computed(() => visibleDropdowns.value.includes('group'));
const showTeacher = computed(() => visibleDropdowns.value.includes('teacher'));
const showUsers = computed(() => visibleDropdowns.value.includes('users'));
const showActivity = computed(() => visibleDropdowns.value.includes('activity'));
// Drawn once the list holds a group: the page says itself that there is none, or why it cannot be read.
const showSpecialistGroup = computed(() =>
  visibleDropdowns.value.includes('specialistGroup') && specialistGroups.groups.length > 0);

/** The teacher's axes, whose lists the store reads on the first route that draws one. */
const hasTeacherAxis = computed(() => showSite.value || showProgram.value || showGroup.value || showTeacher.value || showUsers.value || showActivity.value);
const hasAnyDropdown = computed(() => hasTeacherAxis.value || showSpecialistGroup.value);

const showSepBeforeProgram = computed(() => showProgram.value && showSite.value);
const showSepBeforeGroup = computed(() => showGroup.value && (showSite.value || showProgram.value));
const showSepBeforeTeacher = computed(() => showTeacher.value && (showSite.value || showProgram.value || showGroup.value));
const showSepBeforeActivity = computed(() => showActivity.value && (showSite.value || showProgram.value || showGroup.value || showTeacher.value));
const showSepBeforeUsers = computed(() => showUsers.value && (showSite.value || showProgram.value || showGroup.value || showTeacher.value || showActivity.value));

// The catalogue behind the Activité axis is loaded on the first route that shows it, not in
// initialize(): every page mounts the bar, and only the Présences sheet reads presence types.
// Not before the signed-in user is known (#1164): until then the bar cannot tell the exam
// supervisor, to whom the catalogue answers 403, from a teacher.
watch(() => showActivity.value && !!coreSdk.me.value, (visible) => {
  if (visible) void store.loadActivityTypes();
}, { immediate: true });

const groupTriggerLabel = computed(() => {
  const group = store.selectedGroup;
  if (!group) return null;
  const showUnconfirmed =
    store.groupStatusFilter === GroupStatus.Unconfirmed ||
    (store.groupStatusFilter === 'all' && group.status === GroupStatus.Unconfirmed);
  return showUnconfirmed ? translate('parcours.context.toolbar.groupInPreparation', { name: group.name }) : group.name;
});

const selectedGroupBadgeLabel = computed(() => groupTriggerLabel.value ?? translate('parcours.common.all'));

// Apply hash → store after init, and on external hash changes.
//
// The `#g*` hash is an OVERRIDE, not the source of truth: when a token is present we apply it,
// but when a token is ABSENT we leave the current selection alone (it persists via the store /
// localStorage). This is what lets the context survive navigating to a route whose link carries
// no hash. Explicit clears go through the dropdown `onXChange` handlers, not this function.
//
// The Activité axis (#1163) has no `#gactivity`: nothing links to a filtered sheet, so it is
// neither read here nor written by the cascade — localStorage alone carries it.
async function applyHashToStore() {
  if (!store.initialized) return;
  const p = hashParams.params.value;
  let scopeChanged = false;
  if (showSite.value && p.gsite) {
    const site = store.findSite(p.gsite);
    if (site && store.selectedSite?.id !== site.id) {
      store.selectedSite = site;
    }
  }
  if (showProgram.value && p.gprogram) {
    const prog = store.findProgram(p.gprogram);
    if (prog && store.selectedProgram?.id !== prog.id) {
      store.selectedProgram = prog;
      scopeChanged = true;
    }
  }
  if (showGroup.value && p.ggroup) {
    const grp = store.findGroup(p.ggroup);
    if (grp && store.selectedGroup?.id !== grp.id) {
      store.selectedGroup = grp;
      scopeChanged = true;
    }
  }
  // A locked filter (#879) ignores a `#gteacher` deep-link naming someone else: their own
  // data is everything it could ever show server-side, so honoring it would desync the badge
  // from what the page actually displays instead of correcting anything.
  if (showTeacher.value && p.gteacher && !store.teacherFilterLockedToSelf) {
    // A program or a group chosen just above cascades: the store's watcher drops the teacher and
    // reads the teachers of the new scope. A link naming both — the « Expirée » line of a
    // student's sequence (#971) — lost its teacher to that, and the list read before may not hold
    // them: the cascade runs first, the new scope's teachers are read, then the teacher is applied.
    if (scopeChanged) {
      await nextTick();
      await store.loadTeachers();
    }
    const teacher = store.findTeacher(p.gteacher);
    if (teacher && store.selectedTeacher?.id !== teacher.id) {
      store.selectedTeacher = teacher;
    }
  }
  // Canonicalize: if any of the resolved entities was matched by name, rewrite hash to its id.
  const canonical: Record<string, string | undefined> = {};
  if (showSite.value && p.gsite && store.selectedSite && p.gsite !== store.selectedSite.id) {
    canonical.gsite = store.selectedSite.id;
  }
  if (showProgram.value && p.gprogram && store.selectedProgram && p.gprogram !== store.selectedProgram.id) {
    canonical.gprogram = store.selectedProgram.id;
  }
  if (showGroup.value && p.ggroup && store.selectedGroup && p.ggroup !== store.selectedGroup.id) {
    canonical.ggroup = store.selectedGroup.id;
  }
  if (showTeacher.value && p.gteacher && store.selectedTeacher && p.gteacher !== store.selectedTeacher.id) {
    canonical.gteacher = store.selectedTeacher.id;
  }
  if (Object.keys(canonical).length > 0) {
    await hashParams.setMany(canonical);
  }
}

// Lazy initialization: the toolbar component is mounted on every page, but pages
// declare which dropdowns they need via route.meta.contextToolbar. Skip the (auth-
// gated) site/program/group/teacher fetches until at least one dropdown is requested — and
// until the signed-in user is known, since the exam supervisor gets no dropdown at all (#1164)
// and cannot be told apart before that. The specialist's « Groupe » (#1445) does not count: its
// list is their own, read by their page, and the teacher's lists answer a specialist 403.
watch(() => hasTeacherAxis.value && !!coreSdk.me.value, async (visible) => {
  if (visible && !store.initialized) {
    await store.initialize();
    await applyHashToStore();
  }
}, { immediate: true });

// React to external hash changes (back/forward, paste).
watch(
  () => [hashParams.params.value.gsite, hashParams.params.value.gprogram, hashParams.params.value.ggroup, hashParams.params.value.gteacher].join('|'),
  () => { void applyHashToStore(); },
);

// The dropdowns emit straight into `select()`: searching the list, writing to the store
// and cascading the fragment parameters live in `useContextSelection`, which the landing
// pages of #723 borrow as is. That is what makes a dashboard click *identical* to a
// selection made here, rather than merely similar.
</script>

<style scoped>
.ctx-toolbar {
  display: flex;
  align-items: center;
  gap: 0;
  padding: 0 10px;
  height: 36px;
  background: transparent;
  pointer-events: auto;
  /* Short of room, the menus shrink rather than the bar overflowing or the header wrapping
     (page-header.vue, #1163). */
  min-width: 0;
}

.ctx-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px;
  min-width: 0;
}

/* The dropdown trigger's box (former .ctx-select) lives in context-dropdown.vue now —
   scoped styles cannot cross the component boundary, so the control styles itself. */

.ctx-pin-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  padding: 0;
  font-size: 10px;
  color: rgba(var(--v-theme-on-surface), 0.35);
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: color 0.15s, background 0.15s;
}

.ctx-pin-btn:hover:not(:disabled) {
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.1);
}

.ctx-pin-btn:disabled {
  opacity: 0.25;
  cursor: not-allowed;
}

.ctx-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 24px;
  padding: 0 10px;
  font-size: 12px;
  font-weight: 500;
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.1);
  border: 1px solid rgba(var(--v-theme-primary), 0.25);
  border-radius: 12px;
  white-space: nowrap;
  max-width: 180px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.ctx-badge:hover:not(:disabled) {
  background: rgba(var(--v-theme-primary), 0.18);
  border-color: rgba(var(--v-theme-primary), 0.45);
}

/* #879: a scoped Enseignant's teacher filter is locked to themselves — no hover affordance,
   no pointer cursor, since there is nothing to click through to. */
.ctx-badge--locked {
  cursor: default;
  opacity: 0.85;
}

.ctx-badge__icon {
  font-size: 10px;
  flex-shrink: 0;
}

.ctx-badge__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}


.ctx-sep {
  display: block;
  width: 1px;
  height: 16px;
  background: rgba(var(--v-theme-primary), 0.16);
  flex-shrink: 0;
}

/* ── Mobile / tablet (#116) ──────────────────────────────────────────────────
   The toolbar is a fixed-height single row of selects sized for desktop, so on a
   tablet/phone it overflows the header (see screenshot). Below the md breakpoint,
   let it take the full width and wrap into a tidy two-per-row grid of full-width,
   touch-sized selects. */
@media (max-width: 959px) {
  .ctx-toolbar {
    flex-wrap: wrap;
    height: auto;
    width: 100%;
    padding: 0;
    gap: 8px;
  }

  .ctx-sep {
    display: none;
  }

  /* The pin (collapse-to-badge) is a desktop space-saver — pointless once the
     controls stack full-width, and its lone icon on the right looked broken. */
  .ctx-pin-btn {
    display: none;
  }

  /* Two filters per row; the select (or pinned badge) fills its cell. */
  .ctx-item {
    flex: 1 1 calc(50% - 4px);
    min-width: 0;
    padding: 0;
  }

  .ctx-badge {
    flex: 1 1 auto;
    min-width: 0;
    max-width: none;
    width: 100%;
    height: 34px;
    font-size: 14px;
    justify-content: flex-start;
    border-radius: 8px;
  }
}
</style>
